import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import {Helmet} from 'react-helmet'
import IndexPage from "./pages/home";
import { InformationCircleIcon } from "@heroicons/react/outline";
import { useState, useEffect } from "react";
import { Alert } from "./unlimited_wordle/components/alerts/Alert";
import { Grid } from "./unlimited_wordle/components/grid/Grid";
import { Keyboard } from "./unlimited_wordle/components/keyboard/Keyboard";
import { AboutModal } from "./unlimited_wordle/components/modals/AboutModal";
import { InfoModal } from "./unlimited_wordle/components/modals/InfoModal";
import { WinModal } from "./unlimited_wordle/components/modals/WinModal";
import { isWordInWordList, isWinningWord, solution } from "./unlimited_wordle/lib/words";
import Dictadle from "./pages/dictadle";
import SpanishWordleUnlimitedApp from "./spanish_wordle_unlimited/App"
import GamesPage from "./pages/games"
import Header from "./components/common/Header";


function App() {
  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [isGameWon, setIsGameWon] = useState(false);
  const [isWinModalOpen, setIsWinModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isWordNotFoundAlertOpen, setIsWordNotFoundAlertOpen] = useState(false);
  const [isGameLost, setIsGameLost] = useState(false);

  useEffect(() => {
    if (isGameWon) {
      setIsWinModalOpen(true);
    }
  }, [isGameWon]);

  const onChar = (value: string) => {
    if (currentGuess.length < 5 && guesses.length < 6) {
      setCurrentGuess(`${currentGuess}${value}`);
    }
  };

  const onDelete = () => {
    setCurrentGuess(currentGuess.slice(0, -1));
  };

  const onEnter = () => {
    if (!isWordInWordList(currentGuess)) {
      setIsWordNotFoundAlertOpen(true);
      return setTimeout(() => {
        setIsWordNotFoundAlertOpen(false);
      }, 2000);
    }

    const winningWord = isWinningWord(currentGuess);

    if (currentGuess.length === 5 && guesses.length < 6 && !isGameWon) {
      setGuesses([...guesses, currentGuess]);
      setCurrentGuess("");

      if (winningWord) {
        return setIsGameWon(true);
      }

      if (guesses.length === 5) {
        setIsGameLost(true);
        return setTimeout(() => {
          setIsGameLost(false);
        }, 2000);
      }
    }
  };

  const WordleUnlimitedPage= ()=> (
    <div className="py-8 max-w-7xl mx-auto sm:px-6 lg:px-8">
      <Helmet>
        {/* 页面标题 */}
        <title>Unlimited Wordle - Play Wordle Without Limits</title>
        <meta 
          name="description" 
          content="Play Unlimited Wordle and enjoy endless word guessing fun! No time limits, no daily restrictions—just unlimited word puzzles to challenge your vocabulary." 
        />
        <meta 
          name="keywords" 
          content="Unlimited Wordle, Wordle game, word puzzle, word guessing game, endless Wordle, Wordle alternatives" 
        />
        <meta name="author" content="GamesLikeWordle" />

        {/* Open Graph for Social Media */}
        <meta property="og:title" content="Unlimited Wordle - Play Wordle Without Limits" />
        <meta 
          property="og:description" 
          content="Enjoy unlimited Wordle games and challenge your vocabulary with endless word puzzles. Play as much as you want with no restrictions!" 
        />
        <meta property="og:url" content="https://www.gameslikewordle.com/wordle-unlimited" />
        <meta property="og:type" content="website" />
        {/* <meta property="og:image" content="https://www.gameslikewordle.com/assets/wordle-unlimited-og.jpg" /> */}

        {/* Twitter SEO */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Unlimited Wordle - Play Wordle Without Limits" />
        <meta 
          name="twitter:description" 
          content="Dive into Unlimited Wordle and enjoy endless word guessing fun! No restrictions, no limits—just pure word puzzle excitement." 
        />
        <meta name="twitter:image" content="https://www.gameslikewordle.com/assets/wordle-unlimited-twitter.jpg" />
        <meta name="twitter:site" content="@GamesLikeWordle" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gameslikewordle.com/wordle-unlimited" />
      </Helmet>
      <Header></Header>

      <Alert message="Word not found" isOpen={isWordNotFoundAlertOpen} />
      <Alert
        message={`You lost, the word was ${solution}`}
        isOpen={isGameLost}
      />
      <div className="flex w-80 mx-auto items-center my-4">
        <h1 className="text-xl grow font-bold">Wordle Unlimited</h1>
        <InformationCircleIcon
          className="h-6 w-6 cursor-pointer"
          onClick={() => setIsInfoModalOpen(true)}
        />
      </div>
      <Grid guesses={guesses} currentGuess={currentGuess} />
      <Keyboard
        onChar={onChar}
        onDelete={onDelete}
        onEnter={onEnter}
        guesses={guesses}
      />
      <WinModal
        isOpen={isWinModalOpen}
        handleClose={() => setIsWinModalOpen(false)}
        guesses={guesses}
      />
      <InfoModal
        isOpen={isInfoModalOpen}
        handleClose={() => setIsInfoModalOpen(false)}
      />
      <AboutModal
        isOpen={isAboutModalOpen}
        handleClose={() => setIsAboutModalOpen(false)}
      />
    </div>
  );
  
  return (
    <Router>
      <Routes>
        <Route path="/" element={<IndexPage />} />
        <Route path="/wordle-unlimited" element={<WordleUnlimitedPage />} />
        <Route path="/dictadle" element={<Dictadle />} />
        <Route path="/spanish-wordle-unlimited" element={<SpanishWordleUnlimitedApp />} />
        <Route path="/games" element={<GamesPage />} />
      </Routes>
    </Router>
  );
}

export default App;
